import type { CSSProperties } from 'react'
import type { QboTransactionSearchRow } from '@/services/qboTransactionSearch'
import type { FieldDefinition, FieldOption } from '@/tableControls'
import { rowHasPendingChanges } from '@/utils/qboTransactionRowEdits'
import { getRowSearchableDescriptionText } from '@/utils/qboTransactionResultFilters'
import { readStoredColumnWidths, saveColumnWidths } from '@/tableControls'

export const QBO_TXN_SEARCH_PAGE_KEY = 'qbo-txn-search'
export const LEGACY_WIDTH_KEY = 'bookkeeper:qbo-txn-search-widths'

export const DEFAULT_WIDTHS: Record<string, number> = {
  select: 44,
  transaction_id: 90,
  doc_number: 110,
  date: 100,
  type: 90,
  contact_name: 140,
  line_item_category: 180,
  line_item_description: 220,
  line_item_location: 180,
  line_item_amount: 100,
  update_status: 120,
}

export const FROZEN_COLUMN_KEYS = [
  'select',
  'transaction_id',
  'doc_number',
  'date',
  'type',
  'contact_name',
] as const

export const FROZEN_DIVIDER_KEY = 'contact_name'

export const ALL_COLUMN_KEYS = [
  'select',
  'transaction_id',
  'doc_number',
  'date',
  'type',
  'contact_name',
  'line_item_category',
  'line_item_description',
  'line_item_location',
  'line_item_amount',
  'update_status',
] as const

export type QboTxnColumnKey = (typeof ALL_COLUMN_KEYS)[number]

export const QBO_TXN_COLUMN_LABELS: Record<QboTxnColumnKey, string> = {
  select: '',
  transaction_id: 'ID',
  doc_number: 'Journal #',
  date: 'Date',
  type: 'Type',
  contact_name: 'Vendor',
  line_item_category: 'Category',
  line_item_description: 'Description',
  line_item_location: 'Location',
  line_item_amount: 'Amount',
  update_status: 'Status',
}

const LOCKED_COLUMN_KEYS = new Set<QboTxnColumnKey>([
  'select',
  'transaction_id',
  'doc_number',
  'date',
  'type',
  'contact_name',
  'line_item_category',
  'line_item_description',
  'line_item_location',
])

const SORTABLE_COLUMN_KEYS = new Set<QboTxnColumnKey>([
  'transaction_id',
  'doc_number',
  'date',
  'type',
  'contact_name',
  'line_item_category',
  'line_item_description',
  'line_item_location',
  'line_item_amount',
  'update_status',
])

const FIELD_TYPES: Partial<Record<QboTxnColumnKey, FieldDefinition['type']>> = {
  date: 'date',
  line_item_amount: 'number',
}

const ROW_FIELD_BY_COLUMN: Partial<Record<QboTxnColumnKey, keyof QboTransactionSearchRow>> = {
  transaction_id: 'transaction_id',
  doc_number: 'doc_number',
  date: 'date',
  type: 'type',
  contact_name: 'contact_name',
  line_item_category: 'line_item_category',
  line_item_description: 'line_item_description',
  line_item_location: 'line_item_location',
  line_item_amount: 'line_item_amount',
  update_status: 'update_status',
}

export const QBO_TXN_DEFAULT_SORT = [{ field: 'date', direction: 'desc' as const }]

export const QBO_TXN_FIELD_DEFINITIONS: FieldDefinition[] = ALL_COLUMN_KEYS.map((key) => {
  const rowField = ROW_FIELD_BY_COLUMN[key]
  return {
    key,
    label: QBO_TXN_COLUMN_LABELS[key] || key,
    type: FIELD_TYPES[key] ?? 'text',
    locked: LOCKED_COLUMN_KEYS.has(key),
    defaultVisible: true,
    sortable: SORTABLE_COLUMN_KEYS.has(key),
    ...(rowField
      ? { accessor: (row: unknown) => (row as QboTransactionSearchRow)[rowField] }
      : {}),
  }
})

export function fixedColumnStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
  }
}

export function migrateLegacyColumnWidths(): void {
  const existing = readStoredColumnWidths(QBO_TXN_SEARCH_PAGE_KEY)
  if (Object.keys(existing).length > 0) return
  try {
    const raw = localStorage.getItem(LEGACY_WIDTH_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const widths: Record<string, number> = {}
    for (const key of ALL_COLUMN_KEYS) {
      const value = parsed[key]
      if (typeof value === 'number' && Number.isFinite(value)) {
        widths[key] = value
      }
    }
    if (Object.keys(widths).length > 0) {
      saveColumnWidths(QBO_TXN_SEARCH_PAGE_KEY, widths)
    }
  } catch {
    /* ignore */
  }
}

export function isPinnedColumn(columnKey: string): boolean {
  return FROZEN_COLUMN_KEYS.includes(columnKey as (typeof FROZEN_COLUMN_KEYS)[number])
}

export function pinnedHeaderClass(columnKey: string): string {
  if (!isPinnedColumn(columnKey)) return ''
  const classes = ['table-sticky-pinned', 'table-sticky-cell']
  if (columnKey === FROZEN_DIVIDER_KEY) classes.push('table-locked-edge')
  return classes.join(' ')
}

export function pinnedCellClass(columnKey: string): string {
  if (!isPinnedColumn(columnKey)) return ''
  const classes = ['table-sticky-pinned', 'table-sticky-cell']
  if (columnKey === FROZEN_DIVIDER_KEY) classes.push('table-locked-edge')
  return classes.join(' ')
}

export function isQboTxnColumnSortable(key: string): boolean {
  return SORTABLE_COLUMN_KEYS.has(key as QboTxnColumnKey)
}

/** FilterBuilderDrawer fields (table filters only — not column picker / sort). */
export function buildQboTxnSearchFilterFields(options: {
  vendorOptions: FieldOption[]
  categoryOptions: FieldOption[]
  locationOptions: FieldOption[]
}): FieldDefinition[] {
  return [
    {
      key: 'date',
      label: 'Date',
      type: 'date',
      filterable: true,
      accessor: (row) => (row as QboTransactionSearchRow).date,
    },
    {
      key: 'contact_name',
      label: 'Vendor',
      type: 'multiSelect',
      filterable: true,
      options: options.vendorOptions,
      accessor: (row) => (row as QboTransactionSearchRow).contact_name ?? '',
    },
    {
      key: 'description',
      label: 'Description',
      type: 'text',
      filterable: true,
      accessor: (row) => getRowSearchableDescriptionText(row as QboTransactionSearchRow),
    },
    {
      key: 'doc_number',
      label: 'Journal #',
      type: 'text',
      filterable: true,
      accessor: (row) => (row as QboTransactionSearchRow).doc_number ?? '',
    },
    {
      key: 'memo',
      label: 'Memo',
      type: 'text',
      filterable: true,
      accessor: (row) => (row as QboTransactionSearchRow).memo ?? '',
    },
    {
      key: 'line_item_description',
      label: 'Line description',
      type: 'text',
      filterable: true,
      accessor: (row) => (row as QboTransactionSearchRow).line_item_description ?? '',
    },
    {
      key: 'line_item_category',
      label: 'Category',
      type: 'multiSelect',
      filterable: true,
      options: options.categoryOptions,
      accessor: (row) => (row as QboTransactionSearchRow).line_item_category ?? '',
    },
    {
      key: 'line_item_location',
      label: 'Location',
      type: 'multiSelect',
      filterable: true,
      options: options.locationOptions,
      accessor: (row) => (row as QboTransactionSearchRow).line_item_location ?? '',
    },
    {
      key: 'line_item_amount',
      label: 'Amount',
      type: 'number',
      filterable: true,
      accessor: (row) => (row as QboTransactionSearchRow).line_item_amount,
    },
    {
      key: 'has_pending_changes',
      label: 'Pending changes',
      type: 'boolean',
      filterable: true,
      accessor: (row) => rowHasPendingChanges(row as QboTransactionSearchRow),
    },
  ]
}
